import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { User } from '../App';
import { FeedbackButton } from './FeedbackButton';

/**
 * If the current URL is a lesson route, return its slug (e.g. "1.1", "8.5").
 * Otherwise return null. We only mount the feedback button on lesson pages.
 */
function getLessonSlug(pathname: string): string | null {
  const patterns = [/^\/preview\/lesson\/([\w.\-]+)$/, /^\/lesson\/([\w.\-]+)$/];
  for (const p of patterns) {
    const m = pathname.match(p);
    if (m) return m[1];
  }
  return null;
}

export default function Layout({
  user,
  onLogout,
  children,
}: {
  user: User | null;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // ignore
    }
    onLogout();
    navigate('/');
  }

  return (
    <div className="flex min-h-screen flex-col bg-ink text-text">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-content items-center justify-between px-6 py-4">
          {user && user.status === 'active' ? (
            <Link to="/dashboard" className="flex items-center gap-2">
              <BrandMark />
              <span className="font-mono text-amber text-sm">constare</span>
              <span className="text-muted text-sm">academy</span>
            </Link>
          ) : (
            <a
              href="https://www.constare.academy/"
              className="flex items-center gap-2"
            >
              <BrandMark />
              <span className="font-mono text-amber text-sm">constare</span>
              <span className="text-muted text-sm">academy</span>
            </a>
          )}
          <nav className="flex items-center gap-4 text-sm">
            {user ? (
              <>
                {user.status === 'active' && (
                  <Link to="/dashboard" className="text-muted hover:text-text">
                    Dashboard
                  </Link>
                )}
                <Link to="/profile" className="text-muted hover:text-text">
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-muted hover:text-text"
                >
                  Sign out
                </button>
              </>
            ) : (
              <Link to="/login" className="text-muted hover:text-text">
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto max-w-content px-6 py-10">{children}</div>
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto max-w-content px-6 py-6 text-xs text-muted">
          © {new Date().getFullYear()} Constare Academy. The ground keeps moving. Learn how to stand.
        </div>
      </footer>

      <LessonFeedbackMount />
    </div>
  );
}

function BrandMark() {
  return (
    <svg
      viewBox="0 0 400 384"
      width="22"
      height="22"
      fill="none"
      aria-hidden="true"
      className="text-amber"
    >
      <g transform="translate(0,384) scale(0.1,-0.1)" fill="currentColor">
        <path d="M1735 3654 c-60 -13 -134 -36 -364 -110 -24 -8 -77 -20 -117 -26 -73 -11 -73 -11 -76 -121 -3 -110 -3 -110 -83 -132 -171 -47 -165 -49 -165 65 0 115 3 113 -115 85 -33 -7 -100 -23 -150 -34 -91 -20 -239 -59 -307 -82 -38 -12 -38 -12 -38 -170 0 -158 0 -158 87 -201 88 -43 88 -43 91 -853 2 -811 2 -811 -88 -854 -90 -43 -90 -43 -90 -194 0 -84 4 -157 8 -163 6 -10 81 -51 325 -178 54 -28 146 -76 205 -107 315 -164 606 -315 887 -458 134 -69 170 -64 385 56 45 24 236 126 340 179 30 16 57 32 58 37 2 4 8 7 14 7 6 0 59 27 118 59 58 33 151 82 206 110 54 28 104 54 110 58 5 5 108 58 227 118 218 110 218 110 220 270 2 159 2 159 -74 200 -119 62 -109 38 -109 271 0 204 0 204 -200 204 -200 0 -200 0 -202 -221 -3 -221 -3 -221 -198 -329 -107 -60 -200 -112 -205 -117 -6 -4 -28 -17 -50 -28 -22 -11 -42 -22 -45 -25 -11 -12 -407 -230 -418 -230 -7 0 -12 -4 -12 -9 0 -26 -43 -20 -107 16 -37 21 -84 47 -103 57 -69 35 -317 173 -365 203 -11 7 -38 22 -60 33 -22 12 -65 37 -95 55 -30 19 -62 38 -70 41 -8 4 -33 19 -55 33 -65 42 -78 50 -117 71 -38 20 -38 20 -38 664 0 644 0 644 39 664 58 30 85 46 91 53 3 3 37 22 75 44 39 21 108 61 155 90 47 28 92 54 100 58 17 8 239 142 255 154 17 13 63 39 162 93 92 51 92 51 135 29 24 -12 45 -24 48 -28 3 -3 22 -15 42 -26 32 -17 103 -58 298 -171 120 -70 242 -142 305 -182 123 -79 210 -136 220 -144 6 -5 12 -97 15 -216 5 -207 5 -207 202 -210 198 -2 198 -2 200 385 3 388 3 388 93 433 90 45 90 45 93 201 2 156 2 156 -40 168 -213 60 -545 138 -553 130 -2 -3 -7 -45 -10 -94 -9 -122 -4 -115 -72 -99 -198 47 -193 43 -193 168 0 102 0 102 -78 113 -43 7 -138 32 -212 56 -293 98 -393 113 -535 81z" />
        <path d="M1836 2987 c-11 -7 -25 -16 -30 -20 -10 -7 -135 -81 -349 -206 -43 -25 -95 -56 -115 -69 -20 -12 -55 -32 -77 -43 -22 -12 -42 -25 -45 -29 -3 -3 -20 -13 -37 -21 -18 -8 -33 -17 -33 -20 0 -3 -18 -14 -40 -24 -22 -10 -40 -21 -40 -24 0 -3 -13 -12 -30 -19 -30 -14 -30 -14 -30 -613 0 -329 4 -599 8 -599 5 0 46 -22 91 -50 45 -28 84 -50 86 -50 2 0 49 -27 105 -60 56 -33 106 -60 111 -60 5 0 9 -4 9 -8 0 -5 19 -17 43 -28 23 -10 44 -21 47 -24 3 -3 87 -52 188 -108 183 -102 183 -102 280 -44 53 33 137 81 186 108 48 27 109 61 135 75 25 15 64 37 86 48 22 12 49 27 60 34 11 7 43 26 70 42 230 136 225 129 225 330 0 185 0 185 -204 185 -204 0 -204 0 -211 -79 -6 -72 -9 -80 -30 -85 -20 -5 -96 -46 -180 -97 -47 -28 -176 -102 -217 -123 -38 -20 -38 -20 -65 0 -16 12 -100 61 -188 109 -88 49 -164 93 -170 97 -5 5 -22 17 -37 29 -28 20 -28 20 -28 368 0 389 -6 358 78 395 23 10 42 23 42 28 0 4 5 8 10 8 6 0 29 12 53 27 98 63 262 153 279 153 7 0 29 -10 48 -23 19 -13 71 -43 115 -67 73 -40 170 -99 252 -153 31 -21 33 -26 33 -82 0 -90 -11 -86 216 -83 199 3 199 3 199 193 0 189 0 189 -65 227 -36 20 -69 41 -75 45 -8 6 -294 180 -377 228 -81 47 -354 195 -359 194 -2 0 -13 -6 -23 -12z" />
      </g>
    </svg>
  );
}

function LessonFeedbackMount() {
  const loc = useLocation();
  const slug = getLessonSlug(loc.pathname);
  if (!slug) return null;
  return <FeedbackButton lessonSlug={slug} />;
}
